import mapboxgl from "mapbox-gl";
import type { PositionResponseOk } from "./types";

export interface TrackerMap {
  update(payload: PositionResponseOk): void;
  destroy(): void;
}

export function createTrackerMap(
  container: HTMLElement,
  token: string,
): TrackerMap {
  mapboxgl.accessToken = token;

  const map = new mapboxgl.Map({
    container,
    style: "mapbox://styles/mapbox/streets-v12",
    center: [-80.0635, 26.6406], // Lake Worth FL fallback
    zoom: 11,
    attributionControl: { compact: true },
  });

  let techMarker: mapboxgl.Marker | null = null;
  let customerMarker: mapboxgl.Marker | null = null;
  let routeLineAdded = false;

  function ensureMarkers(payload: PositionResponseOk) {
    if (!techMarker) {
      const el = document.createElement("div");
      el.className = "tech-pin";
      el.style.cssText = `
        width: 28px; height: 28px; border-radius: 50%;
        background: #1d6f54; border: 3px solid #fff;
        box-shadow: 0 0 0 2px rgba(29,111,84,0.35), 0 2px 6px rgba(0,0,0,0.25);
      `;
      techMarker = new mapboxgl.Marker({ element: el })
        .setLngLat([payload.tech.lng, payload.tech.lat])
        .addTo(map);
    } else {
      techMarker.setLngLat([payload.tech.lng, payload.tech.lat]);
    }

    if (!customerMarker) {
      const el = document.createElement("div");
      el.className = "customer-pin";
      el.style.cssText = `
        width: 22px; height: 22px; border-radius: 4px;
        background: #f59e0b; border: 2px solid #fff;
        box-shadow: 0 2px 6px rgba(0,0,0,0.25);
      `;
      customerMarker = new mapboxgl.Marker({ element: el })
        .setLngLat([payload.customer.lng, payload.customer.lat])
        .addTo(map);
    }
  }

  function ensureRouteLine(payload: PositionResponseOk) {
    const data: GeoJSON.Feature<GeoJSON.LineString> = {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: [
          [payload.tech.lng, payload.tech.lat],
          [payload.customer.lng, payload.customer.lat],
        ],
      },
    };

    if (!routeLineAdded) {
      map.addSource("tech-to-customer", { type: "geojson", data });
      map.addLayer({
        id: "tech-to-customer-line",
        type: "line",
        source: "tech-to-customer",
        paint: {
          "line-color": "#1d6f54",
          "line-width": 3,
          "line-dasharray": [2, 2],
          "line-opacity": 0.7,
        },
      });
      routeLineAdded = true;
    } else {
      const src = map.getSource("tech-to-customer") as mapboxgl.GeoJSONSource;
      src.setData(data);
    }
  }

  function fitBounds(payload: PositionResponseOk) {
    const bounds = new mapboxgl.LngLatBounds()
      .extend([payload.tech.lng, payload.tech.lat])
      .extend([payload.customer.lng, payload.customer.lat]);
    map.fitBounds(bounds, {
      padding: { top: 60, right: 60, bottom: 80, left: 60 },
      duration: 600,
      maxZoom: 14,
    });
  }

  let firstFitDone = false;
  let mapReady = false;
  const pendingUpdates: PositionResponseOk[] = [];

  map.on("load", () => {
    mapReady = true;
    while (pendingUpdates.length) {
      apply(pendingUpdates.shift()!);
    }
  });

  function apply(payload: PositionResponseOk) {
    ensureMarkers(payload);
    if (payload.status !== "complete") {
      ensureRouteLine(payload);
    }
    if (!firstFitDone) {
      fitBounds(payload);
      firstFitDone = true;
    }
  }

  return {
    update(payload) {
      if (!mapReady) {
        pendingUpdates.push(payload);
        return;
      }
      apply(payload);
    },
    destroy() {
      map.remove();
    },
  };
}
